import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ArrowDown, ArrowUp, Check, ChevronDown, CirclePlus, PenIcon, Trash2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent } from '@radix-ui/react-dropdown-menu';
import { Button } from '../../../components/ui/button';
import { DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import Input from '../../../components/ui/input';
import Separator from '../../../components/ui/separator';
import useTourPackage from '../../../stores/useTourPackage';
import { ITourPackage } from '../../../types/types';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';
import Skeleton from '../../../components/ui/skeleton';
import useMemoizedValue from '../../../hooks/useMemoizedValue';
import InfiniteScrollObserver from '../../../components/InfiniteScrollObserver';

interface TourCardProps {
  id?: number;
  name: string;
  duration: string;
  price: number;
  imageUrl: string;
  onEdit: () => void;
  onDelete: () => void;
  isSelected?: boolean;
  onSelect?: () => void;
  isSelectMode?: boolean;
}

function formatPrice(price: number) {
  return price.toLocaleString('id-ID');
}

function TourCard({
  id,
  name,
  duration,
  price,
  imageUrl,
  onDelete,
  onEdit,
  isSelectMode,
  isSelected,
  onSelect,
}: TourCardProps) {
  const formattedPrice = useMemoizedValue(() => formatPrice(price || 0), [formatPrice]);

  return (
    <button
      type="button"
      className={`sm:h-72 h-52 border border-border overflow-hidden bg-cover rounded-lg cursor-pointer hover:drop-shadow-md bg-center transition-all duration-300 ease-in-out ${
        isSelected ? 'ring-2 ring-blue-500' : ''
      }`}
      style={{ backgroundImage: `url(${imageUrl || '/assets/no-data-image.svg'})` }}
      onClick={isSelectMode ? onSelect : undefined}
    >
      <div className="h-full w-full p-1 sm:p-2 bg-gradient-to-t justify-between from-primary/80 to-transparent text-white flex items-center flex-col hover:bg-black/30 transition-all duration-300 ease-in-out">
        <div className="flex w-full justify-end sm:p-2 p-1">
          {!isSelectMode ? (
            <div className="flex gap-2 px-2 bg-neutral-50/80 w-fit py-1.5 items-center border border-border rounded-lg">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <Trash2 className="h-4 text-red-600" />
                <span className="sr-only">Delete</span>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <PenIcon className="h-4 text-primary" />
                <span className="sr-only">Edit</span>
              </button>
            </div>
          ) : (
            isSelected && (
              <div className="bg-blue-500 rounded-full p-1">
                <Check className="h-4 w-4 text-white" />
              </div>
            )
          )}
        </div>
        <div className="flex flex-col w-full  rounded-md drop-shadow-lg sm:gap-2 gap-1 sm:p-3 p-2">
          <div title={name} className="flex flex-col items-start gap-1 font-semibold">
            <h1 className="sm:text-xl text-lg overflow-hidden whitespace-nowrap text-ellipsis">{name}</h1>
            <p className="sm:text-sm text-xs uppercase">{duration}</p>
          </div>
          <Separator />
          <div className="flex flex-col items-start">
            <p className="sm:text-[0.5rem] text-[0.4rem] font-medium">Mulai dari</p>
            <div className="sm:text-xl text-base gap-1 flex text-currency font-semibold">
              <p>IDR</p>
              <h1>{formattedPrice}</h1>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

export default function ListTour() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const { tours, fetchTours, removeLoading, isLoadingNextPage, hasMore, page, fetchLoading, removeTour } =
    useTourPackage();
  const [selectedTour, setSelectedTour] = useState<{ id: number; name: string } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [sortBy, setSortBy] = useState<'destination_tour' | 'created_at' | 'edited_at'>(() => {
    const savedSortBy = localStorage.getItem('tourSortBy');
    return savedSortBy ? (savedSortBy as 'destination_tour' | 'created_at' | 'edited_at') : 'edited_at';
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    const savedSortOrder = localStorage.getItem('tourSortOrder');
    return savedSortOrder ? (savedSortOrder as 'desc' | 'asc') : 'desc';
  });

  useEffect(() => {
    localStorage.setItem('tourSortBy', sortBy);
    localStorage.setItem('tourSortOrder', sortOrder);
  }, [sortBy, sortOrder]);

  useEffect(() => {
    if (tours.length === 0) {
      fetchTours(1, true);
    }
  }, [fetchTours, tours]);

  const handleLoadMore = () => {
    fetchTours(page + 1, false);
  };

  const handleTabClick = () => {
    searchParams.get('tab');
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', 'form');
      return newParams;
    });
  };

  const handleEdit = (id: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', 'form');
      newParams.set('edit', String(id));
      return newParams;
    });
  };

  const handleDeleteClick = (id: number, name: string) => {
    setSelectedTour({ id, name });
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedTour) {
      await removeTour(selectedTour.id);
      setIsModalOpen(false);
      setSelectedTour(null);
    } else if (selectedIds.length > 0) {
      const deleteWithDelay = async (ids: number[], index = 0) => {
        if (index >= ids.length) {
          setSelectedIds([]);
          setIsModalOpen(false);
          setIsSelectMode(false);
          return;
        }

        await removeTour(ids[index]);
        setTimeout(() => deleteWithDelay(ids, index + 1), 200);
      };

      await deleteWithDelay(selectedIds);
    }
  };

  const handleSortChange = (field: 'destination_tour' | 'created_at' | 'edited_at') => {
    setSortBy(field);
  };

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
  };

  const toggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    setSelectedIds([]);
  };

  const handleSelect = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]));
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length > 0) {
      setIsModalOpen(true);
    }
  };

  const filteredTour = useMemoizedValue(() => {
    if (!tours) return [];
    return tours
      .filter((tour) => tour.destination_tour.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => {
        const valueA = a[sortBy as keyof ITourPackage];
        const valueB = b[sortBy as keyof ITourPackage];

        if (sortBy === 'destination_tour') {
          return sortOrder === 'asc'
            ? String(valueA).localeCompare(String(valueB))
            : String(valueB).localeCompare(String(valueA));
        }
        return sortOrder === 'asc'
          ? new Date(valueA as string).getTime() - new Date(valueB as string).getTime()
          : new Date(valueB as string).getTime() - new Date(valueA as string).getTime();
      });
  }, [tours, searchTerm, sortBy, sortOrder]);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-2">
        <div className="flex justify-between items-center w-full">
          <h1 className="font-medium text-md">Manage Tour Package</h1>
          <div className="flex gap-2">
            {isSelectMode && (
              <Button variant="destructive" onClick={handleDeleteSelected} disabled={selectedIds.length === 0}>
                <Trash2 size={16} />
                <span className="hidden md:inline ml-1">Delete Selected ({selectedIds.length})</span>
              </Button>
            )}
            <Button variant="outline" onClick={toggleSelectMode}>
              {isSelectMode ? 'Cancel' : 'Select'}
            </Button>
            {!isSelectMode && (
              <Button variant="default" onClick={handleTabClick}>
                <CirclePlus size={16} />
                <span className="hidden md:inline">Create New Product</span>
              </Button>
            )}
          </div>
        </div>
        <div className="flex justify-between items-center w-full md:flex-row flex-col">
          <Input
            placeholder="Search by name"
            className="lg:max-w-sm w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="flex justify-between md:w-fit w-full">
            <Button
              variant="ghost"
              type="button"
              className="p-2 hover:bg-neutral-50 rounded-lg"
              onClick={toggleSortOrder}
            >
              <span className="sr-only">Sort Order</span>
              {sortOrder === 'asc' ? <ArrowUp className="h-4" /> : <ArrowDown className="h-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-2 hover:bg-neutral-50 text-sm rounded-lg flex gap-1">
                  {sortBy === 'destination_tour' && 'Nama'}
                  {sortBy === 'created_at' && 'Dibuat'}
                  {sortBy === 'edited_at' && 'Terakhir diubah'}
                  <ChevronDown className="h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-border bg-white shadow border p-1 z-50 rounded-lg">
                <DropdownMenuItem>
                  <button type="button" onClick={() => handleSortChange('destination_tour')}>
                    Nama
                  </button>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <button type="button" onClick={() => handleSortChange('created_at')}>
                    Dibuat
                  </button>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <button type="button" onClick={() => handleSortChange('edited_at')}>
                    Terakhir diubah
                  </button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      {fetchLoading ? (
        <section className="grid sm:grid-cols-[repeat(auto-fill,_minmax(12rem,_1fr))] grid-cols-[repeat(auto-fill,_minmax(8rem,_1fr))] gap-2 w-full">
          <Skeleton className="sm:h-72 h-48" />
          <Skeleton className="sm:h-72 h-48" />
          <Skeleton className="sm:h-72 h-48" />
          <Skeleton className="sm:h-72 h-48" />
          <Skeleton className="sm:h-72 h-48" />
        </section>
      ) : (
        <main className="grid sm:grid-cols-[repeat(auto-fill,_minmax(12rem,_1fr))] grid-cols-[repeat(auto-fill,_minmax(8rem,_1fr))] gap-2 w-full">
          {filteredTour.length > 0 ? (
            filteredTour.map((tour) => (
              <TourCard
                key={tour.tour_id}
                name={tour.destination_tour}
                duration={tour.duration_tour}
                price={tour.price_tour}
                imageUrl={tour.image_tour[0] || '/assets/no-data-image.svg'}
                onEdit={() => handleEdit(tour.tour_id)}
                onDelete={() => handleDeleteClick(tour.tour_id, tour.destination_tour)}
                isSelectMode={isSelectMode}
                isSelected={selectedIds.includes(tour.tour_id)}
                onSelect={() => handleSelect(tour.tour_id)}
              />
            ))
          ) : (
            <p className="px-3 py-2 col-span-full text-center text-neutral-300">No data</p>
          )}
          {isLoadingNextPage && <Skeleton className="sm:h-72 h-48" />}
        </main>
      )}

      <InfiniteScrollObserver
        hasMore={hasMore}
        isLoading={fetchLoading || isLoadingNextPage}
        onLoadMore={handleLoadMore}
      />
      <ConfirmDeleteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={selectedTour ? selectedTour.name : `${selectedIds.length} selected items`}
        loading={removeLoading}
      />
    </div>
  );
}
